import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SingleResponseModel } from '../../modules_v2/common/models/single-response.model';
import { TaxFieldOriginModel } from './tax-field-origin/tax-field-origin.model';
import {
  Model193,
  Model193FieldOriginQuery,
  Model193ListQuery,
  Model193Recipient,
  Model193RecipientList,
  Model193Validation,
  normalizarModelo193,
  normalizarPerceptor193
} from './model-193/taxes-193.model';

@Injectable({
  providedIn: 'root',
})
export class Model193Service {
  private readonly http = inject(HttpClient);

  private get base(): string {
    return `${environment.apiUrl}/api/v2/tax-forms/193`;
  }

  public async calculate(year: number | string, type?: string, receiptNumber?: string): Promise<Model193> {
    let params = new HttpParams().set('year', year.toString());

    if (type) params = params.set('type', type);
    if (receiptNumber) params = params.set('receiptNumber', receiptNumber);

    const response = await firstValueFrom(
      this.http.get<SingleResponseModel<Model193>>(`${this.base}/calculate`, { params }));

    return normalizarModelo193(response.value);
  }

  public async recalculate(data: Model193): Promise<Model193> {
    const response = await firstValueFrom(
      this.http.post<SingleResponseModel<Model193>>(`${this.base}/recalculate`, data));

    return normalizarModelo193(response.value);
  }

  public async validate(data: Model193): Promise<Model193Validation[]> {
    const response = await firstValueFrom(
      this.http.post<SingleResponseModel<Model193Validation[]>>(`${this.base}/validate`, data));

    return response.value || [];
  }

  public async getFile(data: Model193): Promise<{ blob: Blob, nombre: string | null }> {
    const response = await firstValueFrom(
      this.http.post(`${this.base}/file`, data, { responseType: 'blob', observe: 'response' }));

    const cabecera = response.headers.get('Content-Disposition') || '';
    const coincidencia = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cabecera);
    const nombre = coincidencia ? decodeURIComponent(coincidencia[1].trim()) : null;

    return { blob: new Blob([response.body], { type: 'text/plain;charset=iso-8859-1' }), nombre };
  }

  public async importFile(contenido: string): Promise<Model193> {
    const response = await firstValueFrom(
      this.http.post<SingleResponseModel<Model193>>(`${this.base}/import`, { contenido }));

    return normalizarModelo193(response.value);
  }

  public async getRecipients(query: Model193ListQuery): Promise<Model193RecipientList> {
    let params = new HttpParams()
      .set('year', query.year.toString())
      .set('page', query.page.toString())
      .set('pageSize', query.pageSize.toString());

    if (query.search) params = params.set('search', query.search);
    if (query.sortBy) params = params.set('sortBy', query.sortBy);
    if (query.sortDirection) params = params.set('sortDirection', query.sortDirection);

    const response = await firstValueFrom(
      this.http.get<SingleResponseModel<Model193RecipientList>>(`${this.base}/recipients`, { params }));

    const lista = response.value;

    return {
      items: (lista?.items || []).map(item => normalizarPerceptor193(item)),
      total: lista?.total || 0,
      page: lista?.page || 1,
      pageSize: lista?.pageSize || 0,
      totalBase: lista?.totalBase || 0,
      totalRetenciones: lista?.totalRetenciones || 0
    };
  }

  public async createRecipient(year: number, recipient: Model193Recipient): Promise<Model193Recipient> {
    const params = new HttpParams().set('year', year.toString());
    const response = await firstValueFrom(
      this.http.post<SingleResponseModel<Model193Recipient>>(`${this.base}/recipients`, recipient, { params }));

    return normalizarPerceptor193(response.value);
  }

  public async updateRecipient(uid: string, recipient: Model193Recipient): Promise<Model193Recipient> {
    const response = await firstValueFrom(
      this.http.put<SingleResponseModel<Model193Recipient>>(`${this.base}/recipients/${uid}`, recipient));

    return normalizarPerceptor193(response.value);
  }

  public async deleteRecipient(uid: string): Promise<void> {
    await firstValueFrom(this.http.delete<SingleResponseModel<boolean>>(`${this.base}/recipients/${uid}`));
  }

  public async getOrigen(data: Model193, campo: string): Promise<TaxFieldOriginModel> {
    const query: Model193FieldOriginQuery = {
      campo,
      taxYear: Number(data.ejercicio) || 0,
      valorEnElFormulario: (data as any)[campo] ?? 0
    };

    const response = await firstValueFrom(
      this.http.post<SingleResponseModel<TaxFieldOriginModel>>(`${this.base}/field-origin`, query));

    return response.value;
  }
}
